import { PERMISSIONS } from '@/api/types';
import type { IconName } from '@/components/ui/Icon';

export interface OpsNavItem {
  to: string;
  label: string;
  /** Match the path exactly (the Home link would otherwise be "active" everywhere). */
  end?: boolean;
  /** Shown when the role holds ANY of these; absent = every signed-in admin. A link that can only 403 is worse than none. */
  anyOf?: string[];
}

export interface OpsNavSection {
  /** Stable key (state, tests). */
  key: string;
  title: string;
  icon: IconName;
  items: OpsNavItem[];
}

/**
 * The console's menu (2026-09-30 redesign): eight groups in the order an operator's day goes — what needs me (Home),
 * the tenants, the customers behind them, money, messaging, the platform they run on, the marketing website, and
 * settings. Gated per docs/api-tenant-ops.md §1: the server re-checks every call; this only keeps an admin from being
 * shown doors that are locked for their role. Paths did not change, so every old link still opens.
 */
export const OPS_NAV: OpsNavSection[] = [
  {
    key: 'home',
    title: 'Home',
    icon: 'home',
    items: [{ to: '/', label: 'Home', end: true }],
  },
  {
    key: 'tenants',
    title: 'Tenants',
    icon: 'building',
    items: [
      { to: '/ops/tenants', label: 'All tenants', anyOf: [PERMISSIONS.hostsRead] },
      { to: '/ops/provisioning/new', label: 'Create a tenant', anyOf: [PERMISSIONS.tenantProvision] },
      { to: '/ops/provisioning', label: 'Setup jobs', end: true, anyOf: [PERMISSIONS.tenantProvision] },
    ],
  },
  {
    key: 'customers',
    title: 'Customers',
    icon: 'users',
    items: [
      { to: '/accounts', label: 'Accounts', anyOf: [PERMISSIONS.analyticsRead] },
      { to: '/notifications', label: 'Notifications', anyOf: [PERMISSIONS.analyticsRead] },
      { to: '/at-risk', label: 'At risk', anyOf: [PERMISSIONS.telemetryRead] },
      { to: '/features', label: 'Feature adoption', anyOf: [PERMISSIONS.telemetryRead] },
      { to: '/explore', label: 'Explore', anyOf: [PERMISSIONS.exploreRead] },
    ],
  },
  {
    key: 'money',
    title: 'Money',
    icon: 'coins',
    items: [
      { to: '/ops/money', label: 'Money', anyOf: [PERMISSIONS.costsRead] },
      { to: '/ops/costs', label: 'Costs', anyOf: [PERMISSIONS.costsRead] },
      { to: '/ops/pricing', label: 'Pricing', anyOf: [PERMISSIONS.billingRead] },
      { to: '/ops/offers', label: 'Offers', anyOf: [PERMISSIONS.billingRead] },
      { to: '/ops/gateways', label: 'Payment gateways', anyOf: [PERMISSIONS.billingRead] },
      { to: '/ops/retention', label: 'Retention', anyOf: [PERMISSIONS.billingRead] },
      { to: '/ops/money-operations', label: 'Money operations', anyOf: [PERMISSIONS.costsRead] },
    ],
  },
  {
    key: 'messaging',
    title: 'Messaging',
    icon: 'message',
    items: [
      { to: '/ops/messaging', label: 'Providers & compliance', anyOf: [PERMISSIONS.messagingRead] },
      { to: '/ops/external-accounts', label: 'External accounts', anyOf: [PERMISSIONS.billingRead] },
    ],
  },
  {
    key: 'platform',
    title: 'Platform',
    icon: 'server',
    items: [
      { to: '/ops/environments', label: 'Environments', anyOf: [PERMISSIONS.hostsRead] },
      { to: '/services', label: 'Services', anyOf: [PERMISSIONS.analyticsRead] },
      { to: '/ops/industry-packs', label: 'Industry packs', anyOf: [PERMISSIONS.tenantProvision] },
      { to: '/ops/provisioning/catalog', label: 'Setup steps', anyOf: [PERMISSIONS.tenantProvision] },
      { to: '/ops/import-keys', label: 'Import keys', anyOf: [PERMISSIONS.environmentsManage] },
    ],
  },
  {
    key: 'website',
    title: 'Website',
    icon: 'globe',
    items: [
      { to: '/website', label: 'Traffic', anyOf: [PERMISSIONS.analyticsRead] },
      { to: '/live', label: 'Live visitors', anyOf: [PERMISSIONS.analyticsRead] },
      { to: '/leads', label: 'Leads', anyOf: [PERMISSIONS.analyticsRead] },
      { to: '/sources', label: 'Sources', anyOf: [PERMISSIONS.analyticsRead] },
    ],
  },
  {
    key: 'settings',
    title: 'Settings',
    icon: 'settings',
    items: [
      { to: '/settings/admins', label: 'Console admins', anyOf: [PERMISSIONS.adminsRead] },
      { to: '/ops/audit', label: 'Activity log', anyOf: [PERMISSIONS.billingRead, PERMISSIONS.messagingRead] },
      { to: '/account/password', label: 'Change password' },
      { to: '/help/glossary', label: 'Glossary' },
    ],
  },
];

/** The sections and items a role may see; a section with nothing visible is dropped whole. */
export function visibleOpsNav(permissions: readonly string[]): OpsNavSection[] {
  const held = new Set(permissions);
  return OPS_NAV
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.anyOf || item.anyOf.some((p) => held.has(p))),
    }))
    .filter((section) => section.items.length > 0);
}
